import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  ShieldCheck,
  Sparkles,
  Search,
  Bot,
  Rocket,
  MailX,
  Play,
  Users,
  Clock,
  CalendarX,
  Plus,
  Send,
  TrendingUp,
  Video,
} from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";
import { API } from "../lib/api";

/* ------------------------------------------------------------------ */
/*  /engine . Alternate landing structure for the same product.        */
/*                                                                     */
/*  Cloned STRUCTURE from getwingman.com/landing/wingman/v2 template   */
/*  (centered hero + trust badges, loose-ends chaos, 4 numbered panels */
/*  alternating, security strip, pricing card, CTA banner, FAQ).      */
/*  All copy and content are Get Virtual Growth's own.                */
/* ------------------------------------------------------------------ */

const GOLD = "#C9A961";
const GOLD_SOFT = "#D4B675";
const CARD_BG = "#121214";
const CARD_BORDER = "rgba(255,255,255,0.08)";
const SLATE = "#94A3B8";

const REVENUE_OPTIONS = [
  { value: "0_10k", label: "$0 - $10k" },
  { value: "10k_30k", label: "$10k - $30k" },
  { value: "30k_plus", label: "$30k+" },
];
const BOTTLENECK_OPTIONS = [
  { value: "cold_leads", label: "Sourcing Cold B2B Leads" },
  { value: "warm_dropoffs", label: "Converting Warm Inbound Drop-offs" },
];

function Reveal({ children, className = "", delay = 0, testid }) {
  const reduced = useReducedMotion();
  const props = reduced
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-15% 0px" },
        transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1], delay },
      };
  return (
    <motion.div className={className} data-testid={testid} {...props}>
      {children}
    </motion.div>
  );
}

/* Hi-tech count-up. Counts from 0 to target when scrolled into view. */
function CountUp({ target, format = (n) => n.toLocaleString(), duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });
  const reduced = useReducedMotion();
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: duration * 1000, bounce: 0 });
  const rounded = useTransform(spring, (v) => format(Math.round(v)));
  const [text, setText] = useState(format(0));
  useEffect(() => {
    if (reduced) { setText(format(target)); return; }
    if (inView) mv.set(target);
    const unsub = rounded.on("change", (v) => setText(v));
    return () => unsub();
  }, [inView, target, reduced, mv, rounded, format]);
  return <span ref={ref}>{text}</span>;
}

/* Scanning grid overlay. Subtle pulsing lines that sweep vertically. */
function ScanGrid() {
  const reduced = useReducedMotion();
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(201,169,97,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.7) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {!reduced && (
        <motion.div
          className="absolute left-0 right-0 h-24"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(201,169,97,0.10) 50%, transparent 100%)",
          }}
          initial={{ y: "-20%" }}
          animate={{ y: "120vh" }}
          transition={{ duration: 6, ease: "linear", repeat: Infinity, repeatDelay: 1 }}
        />
      )}
    </div>
  );
}

/* Blinking terminal cursor. */
function Cursor() {
  const reduced = useReducedMotion();
  if (reduced) return <span style={{ color: "#C9A961" }}>_</span>;
  return (
    <motion.span
      style={{ color: "#C9A961", display: "inline-block" }}
      animate={{ opacity: [1, 0, 1] }}
      transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
    >
      _
    </motion.span>
  );
}

/* Waveform bars for video mocks */
function EngineWaveform({ bars = 22, active = true }) {
  const reduced = useReducedMotion();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "3px", height: "24px" }}>
      {Array.from({ length: bars }).map((_, i) => (
        <motion.span
          key={i}
          style={{ width: "2px", background: GOLD, borderRadius: "2px", display: "block" }}
          animate={reduced || !active ? { height: 6 } : { height: [4 + (i % 5) * 3, 10 + (i % 7) * 3, 4 + (i % 5) * 3] }}
          transition={{ duration: 0.9 + (i % 4) * 0.15, delay: i * 0.05, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* Animated video mock card in the champagne-on-dark aesthetic. */
function EngineVideoCard({ card, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-15%" });
  const reduced = useReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!inView || reduced) return undefined;
    const t = setInterval(() => setProgress((p) => (p + 1) % 101), 60);
    return () => clearInterval(t);
  }, [inView, reduced]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`engine-video-${card.id}`}
      style={{
        aspectRatio: "3/4",
        borderRadius: "20px",
        background: `linear-gradient(160deg, ${card.tint1} 0%, ${card.tint2} 100%)`,
        border: `1px solid ${CARD_BORDER}`,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)",
      }}
    >
      {/* Champagne grid overlay */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, opacity: 0.10,
        backgroundImage: "linear-gradient(rgba(201,169,97,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.5) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }} />

      {/* Live badge */}
      <div style={{
        position: "absolute", top: "14px", left: "14px",
        display: "inline-flex", alignItems: "center", gap: "6px",
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
        border: CARD_BORDER, padding: "4px 10px", borderRadius: "999px",
        fontSize: "10px", color: "#FFFFFF", fontWeight: 700, letterSpacing: "0.08em",
      }}>
        <motion.span
          style={{ height: "6px", width: "6px", borderRadius: "50%", background: "#EF4444" }}
          animate={reduced ? {} : { opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.2, repeat: Infinity }}
        />
        LIVE
      </div>

      {/* Duration */}
      <div style={{
        position: "absolute", top: "14px", right: "14px",
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
        border: CARD_BORDER, padding: "4px 10px", borderRadius: "999px",
        fontSize: "11px", color: "#FFFFFF", fontFamily: "monospace",
      }}>{card.duration}</div>

      {/* Center avatar */}
      <div style={{
        position: "absolute", top: "26%", left: "50%", transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "14px",
      }}>
        <div style={{ position: "relative" }}>
          <div style={{
            height: "84px", width: "84px", borderRadius: "50%",
            background: `linear-gradient(135deg, ${card.tint2}, ${card.tint1})`,
            border: `2px solid ${GOLD}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#FFFFFF", fontSize: "30px", fontWeight: 700, letterSpacing: "-0.02em",
            boxShadow: `0 0 24px ${card.tint2}`,
          }}>
            {card.initials}
          </div>
          <motion.span
            aria-hidden
            animate={reduced ? {} : { scale: [1, 1.4], opacity: [0.6, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
            style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${GOLD}` }}
          />
          <div style={{
            position: "absolute", bottom: "-4px", right: "-4px",
            height: "28px", width: "28px", borderRadius: "50%",
            background: GOLD, border: `2px solid ${CARD_BG}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Play className="h-3 w-3" style={{ color: "#000000", marginLeft: 1 }} fill="currentColor" />
          </div>
        </div>
        <div style={{
          background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
          padding: "8px 14px", borderRadius: "999px",
          fontSize: "12px", color: "#FFFFFF", fontWeight: 500, border: CARD_BORDER,
        }}>
          Hi {card.firstName},
        </div>
        <EngineWaveform bars={20} active={inView} />
      </div>

      {/* Progress */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", background: "rgba(0,0,0,0.4)" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: GOLD, transition: "width 60ms linear" }} />
      </div>

      {/* Caption */}
      <div style={{
        position: "absolute", bottom: "18px", left: "14px", right: "14px",
        padding: "12px 14px", borderRadius: "12px",
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(10px)",
        border: CARD_BORDER,
      }}>
        <p style={{ fontSize: "9px", letterSpacing: "0.14em", color: GOLD, textTransform: "uppercase", fontWeight: 700 }}>
          Sent to
        </p>
        <p style={{ marginTop: "3px", fontSize: "13px", color: "#FFFFFF", fontWeight: 500 }}>{card.prospect}</p>
        <p style={{ marginTop: "3px", fontSize: "10px", color: SLATE }}>{card.title}</p>
      </div>
    </motion.div>
  );
}

const ENGINE_VIDEOS = [
  { id: "priya", initials: "PS", firstName: "Priya", prospect: "Priya Sharma", title: "VP Growth . Hospitality Group", duration: "0:47", tint1: "#1E1B3A", tint2: "#4C1D95" },
  { id: "james", initials: "JN", firstName: "James", prospect: "James Northcott", title: "Founder . B2B Consultancy", duration: "0:52", tint1: "#1A2B3A", tint2: "#0F5E7A" },
  { id: "leila", initials: "LO", firstName: "Leila", prospect: "Leila Okafor", title: "Head of Ops . Health Clinics", duration: "0:41", tint1: "#3A2E1B", tint2: "#7A5A1F" },
];

function PrimaryCTA({ children, onClick, type = "button", testid, to }) {
  const cls =
    "inline-flex items-center gap-2 font-semibold text-black bg-white hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(255,255,255,0.10)] transition-all duration-300";
  const style = { padding: "16px 32px", borderRadius: "8px", fontSize: "16px" };
  if (to) {
    return (
      <Link to={to} data-testid={testid} className={cls} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <button
      type={type}
      onClick={onClick}
      data-testid={testid}
      className={cls}
      style={style}
    >
      {children}
    </button>
  );
}

function SecondaryCTA({ children, onClick, to, testid }) {
  const cls =
    "inline-flex items-center gap-2 font-semibold text-white bg-transparent hover:-translate-y-0.5 hover:bg-white/[0.04] transition-all duration-300";
  const style = {
    padding: "15px 30px",
    borderRadius: "8px",
    fontSize: "16px",
    border: "1px solid rgba(255,255,255,0.18)",
  };
  if (to) {
    return (
      <Link to={to} data-testid={testid} className={cls} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={cls} style={style}>
      {children}
    </button>
  );
}

/* TwinVideoCard — silent portrait loop by default, click reveals full pitch
   with audio. Uses two overlaid <video> elements so the switch is instant. */
function TwinVideoCard({ posterSrc, loopMp4, loopWebm, pitchSrc, gold, cardBorder, slate }) {
  const [playing, setPlaying] = React.useState(false);
  const pitchRef = React.useRef(null);

  const startPitch = () => {
    setPlaying(true);
    // Give the DOM a tick, then play the pitch video unmuted.
    setTimeout(() => {
      const v = pitchRef.current;
      if (!v) return;
      v.muted = false;
      v.currentTime = 0;
      v.play().catch(() => {
        // Autoplay-with-audio blocked. Fall back to muted play so user still sees motion.
        v.muted = true;
        v.play().catch(() => {});
      });
    }, 40);
  };

  return (
    <div
      className="relative mx-auto rounded-3xl overflow-hidden group"
      data-testid="engine-twin-video"
      style={{
        maxWidth: 520,
        background: "linear-gradient(180deg, #14141A 0%, #0A0A0A 100%)",
        border: `1px solid ${cardBorder}`,
        boxShadow: "0 40px 100px -30px rgba(201,169,97,0.25)",
        aspectRatio: "720 / 940",
      }}
    >
      {/* Ambient glow around the card */}
      <div
        aria-hidden
        className="absolute -inset-8 pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 30%, rgba(201,169,97,0.18) 0%, transparent 60%)",
          filter: "blur(24px)",
        }}
      />

      {/* Silent looping preview (default view) */}
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={posterSrc}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: playing ? 0 : 1, transition: "opacity 400ms ease" }}
        aria-hidden={playing}
        data-testid="engine-twin-loop"
      >
        <source src={loopWebm} type="video/webm" />
        <source src={loopMp4} type="video/mp4" />
      </video>

      {/* Full pitch with audio (revealed on click) */}
      <video
        ref={pitchRef}
        controls={playing}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        playsInline
        preload="none"
        poster={posterSrc}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: playing ? 1 : 0, transition: "opacity 400ms ease", pointerEvents: playing ? "auto" : "none" }}
        data-testid="engine-twin-pitch"
      >
        <source src={pitchSrc} type="video/mp4" />
      </video>

      {/* Play overlay (visible only in preview state) */}
      {!playing && (
        <button
          type="button"
          onClick={startPitch}
          aria-label="Play the full pitch from Krishna"
          data-testid="engine-twin-play"
          className="absolute inset-0 w-full h-full flex flex-col items-end justify-end text-left p-6 md:p-8 cursor-pointer group/play"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 90%, rgba(0,0,0,0.75) 100%)",
          }}
        >
          <span
            className="inline-flex items-center gap-3 rounded-full backdrop-blur-md group-hover/play:-translate-y-0.5 transition-transform"
            style={{
              background: "rgba(255,255,255,0.92)",
              color: "#0A0A0A",
              padding: "10px 18px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            <span
              className="h-6 w-6 rounded-full inline-flex items-center justify-center"
              style={{ background: gold }}
            >
              <Play className="h-3 w-3" style={{ color: "#000", marginLeft: 1 }} />
            </span>
            Hear the full pitch (1:46)
          </span>
          <span
            className="mt-3 font-mono self-start"
            style={{
              fontSize: "10px",
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              color: gold,
            }}
          >
            Live · Muted preview
          </span>
        </button>
      )}
    </div>
  );
}

function StickyDiagnoseCTA() {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 900);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <Link
      to="/diagnose"
      data-testid="engine-sticky-diagnose"
      className="hidden lg:inline-flex fixed z-40 bottom-24 right-8 items-center gap-2 rounded-full font-semibold text-black bg-white shadow-[0_12px_30px_rgba(201,169,97,0.35)] hover:-translate-y-0.5 transition-all duration-300"
      style={{ padding: "12px 20px", fontSize: "14px" }}
    >
      <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
      Diagnose your pipeline
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

const PILL_STYLE = {
  base: {
    background: CARD_BG,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: "999px",
    padding: "8px 14px",
    fontSize: "12px",
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
};

/* Chaotic floating pain-pill used inside the "loose ends" section. */
function LoosePill({ label, Icon, badge, tone = "gray", style }) {
  const toneMap = {
    gray: { color: SLATE, badgeBg: "rgba(255,255,255,0.06)", badgeColor: "#FFFFFF" },
    gold: { color: GOLD, badgeBg: "rgba(201,169,97,0.15)", badgeColor: GOLD },
    red: { color: "#F87171", badgeBg: "rgba(248,113,113,0.15)", badgeColor: "#F87171" },
  };
  const t = toneMap[tone];
  return (
    <div
      className="inline-flex items-center gap-3"
      style={{
        background: CARD_BG,
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: "999px",
        padding: "10px 16px",
        fontSize: "13px",
        color: "#FFFFFF",
        ...style,
      }}
    >
      <Icon className="h-4 w-4" style={{ color: t.color }} />
      <span>{label}</span>
      <span
        className="ml-1 font-semibold"
        style={{
          background: t.badgeBg,
          color: t.badgeColor,
          padding: "2px 8px",
          borderRadius: "999px",
          fontSize: "11px",
        }}
      >
        {badge}
      </span>
    </div>
  );
}

/* Alternating numbered feature panel. */
function NumberedPanel({ index, title, body, kicker, testid, flip = false, Icon, stat, deliverables = [] }) {
  return (
    <Reveal
      className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
      testid={testid}
    >
      <div className={flip ? "md:order-2" : ""}>
        <p
          className="font-bold mb-4"
          style={{ fontSize: "48px", color: GOLD, lineHeight: 1, letterSpacing: "-0.02em" }}
          data-testid={`${testid}-index`}
        >
          {index}
        </p>
        <p
          className="font-semibold mb-3"
          style={{
            fontSize: "12px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: GOLD,
          }}
        >
          {kicker}
        </p>
        <h3
          className="font-semibold text-white"
          style={{
            fontSize: "clamp(24px, 3vw, 32px)",
            letterSpacing: "-0.02em",
            lineHeight: 1.25,
          }}
        >
          {title}
        </h3>
        <p
          className="mt-5 max-w-lg"
          style={{ fontSize: "16px", lineHeight: 1.6, color: SLATE }}
        >
          {body}
        </p>
      </div>

      {/* Visual side - real deliverables, not a decorative void */}
      <div className={flip ? "md:order-1" : ""}>
        <div
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: "4/3",
            background: "linear-gradient(135deg, #14141A 0%, #0A0A0A 100%)",
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: "20px",
            padding: "36px",
          }}
          data-testid={`${testid}-card`}
        >
          <div
            aria-hidden
            className="absolute -top-16 -right-16 h-64 w-64 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(201,169,97,0.20) 0%, transparent 70%)",
              filter: "blur(80px)",
            }}
          />

          {/* Icon + stat pill */}
          <div className="flex items-center justify-between">
            <span
              className="inline-grid place-items-center h-14 w-14 rounded-2xl"
              style={{
                background: "rgba(201,169,97,0.10)",
                border: `1px solid ${GOLD}55`,
                color: GOLD,
              }}
            >
              {Icon ? <Icon className="h-6 w-6" /> : null}
            </span>
            {stat && (
              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono"
                style={{
                  background: "rgba(0,0,0,0.35)",
                  border: `1px solid ${CARD_BORDER}`,
                  fontSize: "10px",
                  letterSpacing: "0.12em",
                  color: GOLD,
                  textTransform: "uppercase",
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
                {stat}
              </span>
            )}
          </div>

          {/* What comes out of this step */}
          <p
            className="mt-6 font-semibold"
            style={{
              fontSize: "10px",
              letterSpacing: "0.14em",
              color: SLATE,
              textTransform: "uppercase",
            }}
          >
            What you get
          </p>
          <ul className="mt-3 space-y-2.5">
            {deliverables.map((d) => (
              <li key={d} className="flex items-start gap-3">
                <Check className="h-3.5 w-3.5 shrink-0 mt-1" style={{ color: GOLD }} />
                <span style={{ fontSize: "14px", lineHeight: 1.5, color: "#FFFFFF" }}>
                  {d}
                </span>
              </li>
            ))}
          </ul>

          <p
            className="absolute bottom-6 right-6 font-mono"
            style={{ fontSize: "10px", color: "rgba(255,255,255,0.30)", letterSpacing: "0.14em", textTransform: "uppercase" }}
          >
            step / {index}
          </p>
        </div>
      </div>
    </Reveal>
  );
}

const NUMBERED_PANELS = [
  {
    index: "01",
    kicker: "Sourcing",
    title: "Hyper-Targeted Prospect Lists",
    body: "We research your exact niche market and mine highly verified data lists containing your dream accounts. Every contact hand-checked, every list refreshed weekly.",
    Icon: Search,
    stat: "Refreshed weekly",
    deliverables: [
      "500 to 1000 verified named prospects a month",
      "Every contact hand-checked against your ICP",
      "Delivered inside a live shared view you can audit",
    ],
  },
  {
    index: "02",
    kicker: "Cloning",
    title: "Digital Twin Training",
    body: "We configure, test, and program your high-definition interactive avatar and clone your natural vocal pitch, so every video sounds and looks like you recorded it live.",
    Icon: Video,
    stat: "One recording session",
    deliverables: [
      "HD digital twin trained on your face and voice",
      "One founder recording session, no ongoing shoots",
      "Ready to send from inside 48 hours",
    ],
  },
  {
    index: "03",
    kicker: "Launch",
    title: "Programmatic Video Drip",
    body: "We sync your pipeline with our custom backend dashboard to programmatically generate and safely drip personalised video emails straight to target inboxes.",
    Icon: Send,
    stat: "Reply desk live",
    deliverables: [
      "Personalised video sent to every named prospect",
      "Delivered from your identity, engineered for inbox not spam",
      "Human replies handled inside two hours",
    ],
  },
  {
    index: "04",
    kicker: "Compounding",
    title: "12,000 Warm Touchpoints A Year",
    body: "Your digital twin pitches 1,000 ideal decision-makers every single month on autopilot. Over 12,000 warm touchpoints a year without you touching the camera.",
    Icon: TrendingUp,
    stat: "Zero camera time",
    deliverables: [
      "1000+ decision-makers reached every month",
      "Meetings booked and dropped on your calendar",
      "Monday briefing digest so you always know what shipped",
    ],
  },
];

const FAQ = [
  {
    q: "How is this different from hiring an SDR agency?",
    a: "Agencies rent you a pod. We build you an owned engine. Your digital twin, your verified list, your dashboard. You keep it if we part ways. Everything is founder-led by Krishna, not junior account managers.",
  },
  {
    q: "How long until first meetings land?",
    a: "First conversations inside week two. First qualified meetings inside week four. Steady flow of eight to twelve qualified conversations per month by day sixty.",
  },
  {
    q: "Do prospects know it is a cloned video?",
    a: "The video is trained on your face and voice, so it is genuinely yours. Every send goes to a named account with a personalised opener. We disclose the setup on the follow-up call, never before.",
  },
  {
    q: "Which tools does the engine plug into?",
    a: "HeyGen for the digital twin, verified B2B data providers for sourcing, your existing CRM or Google Sheets for pipeline, and a secure backend dashboard we build for you.",
  },
  {
    q: "What is the pricing?",
    a: "$10,000 per month. First ninety days is proof, not lock-in. Cancel any time after day 90 in writing.",
  },
];

export default function Engine() {
  usePageTitle("Automated Outbound Video Engine");

  return (
    <main
      className="text-white"
      style={{
        background: "linear-gradient(180deg, #050505 0%, #0A0A0A 100%)",
        fontFamily:
          "'Inter', 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        scrollBehavior: "smooth",
      }}
      data-testid="engine-page"
    >
      {/* ============ 1 . HERO CENTERED ============ */}
      <section
        className="relative overflow-hidden"
        style={{ paddingTop: "140px", paddingBottom: "80px" }}
        data-testid="engine-hero"
      >
        <ScanGrid />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(201,169,97,0.16) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-[1080px] mx-auto px-6 text-center">
          <Reveal>
            <div
              className="inline-flex items-center gap-2 mb-8"
              style={PILL_STYLE.base}
            >
              <Sparkles className="h-3 w-3" style={{ color: GOLD }} />
              Built by Get Virtual Growth
              <span style={{ color: SLATE, marginLeft: 6 }}>. For high-ticket B2B brands</span>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <h1
              className="font-bold text-white mx-auto max-w-4xl"
              style={{
                fontSize: "clamp(36px, 6.4vw, 68px)",
                lineHeight: 1.1,
                letterSpacing: "-0.03em",
              }}
              data-testid="engine-hero-title"
            >
              A calendar full of decision-makers.{" "}
              <span
                style={{
                  background: `linear-gradient(180deg, #FFFFFF 0%, ${GOLD} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Without you touching cold email.
              </span>
            </h1>
          </Reveal>

          <Reveal delay={0.1}>
            <p
              className="mt-6 mx-auto max-w-2xl"
              style={{ fontSize: "18px", lineHeight: 1.6, color: SLATE }}
              data-testid="engine-hero-subtitle"
            >
              Built for founder-led coaches, consultants and recruiters doing $30K+ a month. We build a video engine in your face and voice that reaches 1,000 named prospects every month. You review one weekly digest and take the meetings that land.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/diagnose"
                data-testid="engine-hero-cta-diagnose"
                className="inline-flex items-center gap-2 font-semibold text-black bg-white hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(255,255,255,0.10)] transition-all duration-300"
                style={{ padding: "16px 32px", borderRadius: "8px", fontSize: "16px" }}
              >
                <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
                Get a free growth diagnosis
                <ArrowRight className="h-4 w-4" />
              </Link>
              <SecondaryCTA to="/build" testid="engine-hero-cta-build">
                See how the engine works
              </SecondaryCTA>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <p
              className="mt-5 mx-auto"
              style={{ fontSize: "13px", color: SLATE }}
              data-testid="engine-hero-fit"
            >
              Not for everyone. Ideal fit: coaches, consultants and recruiters at $30K to $500K a month who sell high-ticket to real humans.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {[
                { Icon: Sparkles, label: "1,000 named prospects a month" },
                { Icon: Check, label: "In your face and voice" },
                { Icon: ShieldCheck, label: "One weekly digest, not daily noise" },
              ].map((b) => (
                <span
                  key={b.label}
                  className="inline-flex items-center gap-2"
                  style={{ fontSize: "12px", color: SLATE }}
                  data-testid={`engine-hero-badge-${b.label.split(" ")[0].toLowerCase()}`}
                >
                  <b.Icon className="h-3.5 w-3.5" style={{ color: GOLD }} />
                  {b.label}
                </span>
              ))}
            </div>
          </Reveal>

          {/* Device mockup below hero */}
          <Reveal delay={0.3}>
            <div
              className="mt-20 mx-auto max-w-4xl relative"
              data-testid="engine-hero-mockup"
            >
              <div
                aria-hidden
                className="absolute -inset-8 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(201,169,97,0.20) 0%, transparent 70%)",
                  filter: "blur(60px)",
                }}
              />
              <div
                className="relative rounded-3xl overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, #1A1A20 0%, #0A0A0A 100%)",
                  border: `1px solid ${CARD_BORDER}`,
                  padding: "20px",
                  boxShadow:
                    "0 60px 120px -40px rgba(201,169,97,0.30), inset 0 1px 2px rgba(255,255,255,0.10)",
                }}
              >
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{
                    background: "#050505",
                    aspectRatio: "16/10",
                    border: `1px solid ${CARD_BORDER}`,
                  }}
                >
                  <div
                    className="flex items-center gap-2 px-5 py-3 border-b"
                    style={{ borderColor: CARD_BORDER, background: "rgba(255,255,255,0.02)" }}
                  >
                    <div className="h-2.5 w-2.5 rounded-full" style={{ background: "#FF5F57" }} />
                    <div className="h-2.5 w-2.5 rounded-full" style={{ background: "#FEBC2E" }} />
                    <div className="h-2.5 w-2.5 rounded-full" style={{ background: "#28C840" }} />
                    <div className="ml-4 font-mono" style={{ fontSize: "11px", color: SLATE }}>
                      engine.getvirtualgrowth.com/pipeline<Cursor />
                    </div>
                  </div>
                  <div className="p-8 grid grid-cols-3 gap-4">
                    {[
                      { label: "Prospects sourced", value: 1024 },
                      { label: "Videos rendered", value: 1024 },
                      { label: "Meetings booked", value: 37 },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className="rounded-xl p-5"
                        style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}
                      >
                        <p style={{ fontSize: "11px", color: SLATE, letterSpacing: "0.05em" }}>
                          {s.label.toUpperCase()}
                        </p>
                        <p
                          className="mt-2 font-bold"
                          style={{
                            fontSize: "clamp(20px, 2.2vw, 32px)",
                            color: "#FFFFFF",
                            letterSpacing: "-0.02em",
                          }}
                        >
                          <CountUp target={s.value} />
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="px-8 pb-6 space-y-2">
                    <p
                      className="font-mono mb-2"
                      style={{ fontSize: "10px", color: SLATE, letterSpacing: "0.15em", textTransform: "uppercase" }}
                    >
                      Sample dashboard rows
                    </p>
                    {[
                      "priya@f&b-hotelgroup.ae . watched 47s",
                      "james@northstar-consult.co.uk . replied",
                      "leila@brava-clinics.com . watched 62s",
                    ].map((row, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between rounded-lg px-4 py-3"
                        style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${CARD_BORDER}` }}
                      >
                        <span className="font-mono" style={{ fontSize: "12px", color: "#FFFFFF" }}>
                          {row}
                        </span>
                        <ShieldCheck className="h-3.5 w-3.5" style={{ color: GOLD }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ 1.5 . MEET THE DIGITAL TWIN (Tavus placeholder) ============ */}
      <section
        className="relative"
        style={{ paddingTop: "60px", paddingBottom: "60px" }}
        data-testid="engine-meet-twin"
      >
        <div className="max-w-[1080px] mx-auto px-6">
          <Reveal>
            <div className="text-center mb-10">
              <p
                className="mb-3 font-mono"
                style={{ fontSize: "11px", letterSpacing: "0.4em", textTransform: "uppercase", color: GOLD }}
              >
                Meet the Digital Twin
              </p>
              <h2
                className="font-bold text-white mx-auto max-w-3xl"
                style={{
                  fontSize: "clamp(26px, 4.2vw, 44px)",
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                }}
              >
                Watch Krishna&apos;s twin{" "}
                <span
                  style={{
                    background: `linear-gradient(180deg, #FFFFFF 0%, ${GOLD} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  pitch you in under two minutes.
                </span>
              </h2>
              <p
                className="mt-4 mx-auto max-w-xl"
                style={{ fontSize: "16px", lineHeight: 1.6, color: SLATE }}
              >
                Same face. Same voice. Different prospect every render. This is the exact video your buyers will receive.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <TwinVideoCard
              posterSrc="/video/krishna-twin-poster.jpg"
              loopMp4="/video/krishna-twin-loop.mp4"
              loopWebm="/video/krishna-twin-loop.webm"
              pitchSrc="/video/krishna-twin-pitch.mp4"
              gold={GOLD}
              cardBorder={CARD_BORDER}
              slate={SLATE}
            />
          </Reveal>

          <Reveal delay={0.2}>
            <div
              className="mt-10 flex flex-col items-center gap-3"
              data-testid="engine-twin-cta"
            >
              <Link
                to="/diagnose"
                data-testid="engine-twin-cta-primary"
                className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                style={{
                  background: GOLD,
                  color: "#0A0A0A",
                  boxShadow: "0 20px 60px -20px rgba(201,169,97,0.55)",
                }}
              >
                <Sparkles className="w-4 h-4" aria-hidden />
                Get your free pipeline diagnosis
                <ArrowRight className="w-4 h-4" aria-hidden />
              </Link>
              <p
                className="text-center max-w-md"
                style={{ fontSize: "12px", lineHeight: 1.5, color: SLATE }}
              >
                60 seconds. No calendar link. You will see the exact bottleneck stopping your named prospects from booking.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ 2 . LOOSE ENDS CHAOS ============ */}
      <section
        className="relative"
        style={{ paddingTop: "100px", paddingBottom: "100px" }}
        data-testid="engine-loose-ends"
      >
        <div className="max-w-[1080px] mx-auto px-6">
          <Reveal>
            <h2
              className="font-bold text-center mx-auto max-w-3xl"
              style={{
                fontSize: "clamp(28px, 4.4vw, 48px)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
              }}
              data-testid="engine-loose-title"
            >
              Your outbound has{" "}
              <span style={{ color: GOLD }}>too many loose ends.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-16 relative min-h-[360px]">
              {/* SVG connector converging to center with wire-trace draw */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 800 360"
                preserveAspectRatio="none"
                aria-hidden
              >
                <defs>
                  <linearGradient id="line-gold" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor={GOLD} stopOpacity="0.4" />
                    <stop offset="1" stopColor={GOLD} stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                {[
                  [80, 60, 400, 180],
                  [720, 80, 400, 180],
                  [120, 300, 400, 180],
                  [680, 300, 400, 180],
                ].map(([x1, y1, x2, y2], i) => (
                  <motion.line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="url(#line-gold)"
                    strokeWidth="1"
                    strokeDasharray="4 6"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 1 }}
                    viewport={{ once: true, margin: "-20%" }}
                    transition={{ duration: 1.6, delay: 0.15 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  />
                ))}
              </svg>

              {/* Floating pain pills */}
              <div className="absolute left-2 md:left-6 top-4">
                <LoosePill label="Cold emails ignored" Icon={MailX} badge="1,268" tone="red" />
              </div>
              <div className="absolute right-2 md:right-6 top-8">
                <LoosePill label="Videos not personalised" Icon={Users} badge="24" tone="gold" />
              </div>
              <div className="absolute left-4 md:left-10 bottom-6">
                <LoosePill label="Follow-ups die" Icon={Clock} badge="89" tone="gray" />
              </div>
              <div className="absolute right-4 md:right-10 bottom-10">
                <LoosePill label="Calendar sits empty" Icon={CalendarX} badge="0" tone="red" />
              </div>

              {/* Center hub with pulsing ring */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="relative rounded-full flex items-center justify-center"
                  style={{
                    width: "160px",
                    height: "160px",
                    background:
                      "radial-gradient(circle, rgba(201,169,97,0.30) 0%, rgba(201,169,97,0.10) 60%, transparent 100%)",
                  }}
                >
                  {/* Expanding pulse rings */}
                  {[0, 1].map((i) => (
                    <motion.span
                      key={i}
                      aria-hidden
                      className="absolute inset-0 rounded-full"
                      style={{ border: `1px solid ${GOLD}` }}
                      initial={{ scale: 0.7, opacity: 0.7 }}
                      animate={{ scale: 1.6, opacity: 0 }}
                      transition={{ duration: 2.4, delay: i * 1.2, repeat: Infinity, ease: "easeOut" }}
                    />
                  ))}
                  <div
                    className="rounded-full flex flex-col items-center justify-center text-center px-4 relative"
                    style={{
                      width: "120px",
                      height: "120px",
                      background: CARD_BG,
                      border: `1px solid ${GOLD}`,
                      boxShadow: "0 20px 50px -10px rgba(201,169,97,0.35)",
                    }}
                    data-testid="engine-loose-hub"
                  >
                    <Sparkles className="h-5 w-5 mb-1" style={{ color: GOLD }} />
                    <p
                      className="font-semibold"
                      style={{ fontSize: "12px", color: "#FFFFFF", letterSpacing: "0.02em" }}
                    >
                      Meet the Engine
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p
              className="text-center mx-auto max-w-2xl mt-8"
              style={{ fontSize: "16px", lineHeight: 1.6, color: SLATE }}
            >
              One dashboard that tightens every loose thread. Sourcing, cloning, sending, tracking, following up. All of it. In one place.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ 3 . NUMBERED PANELS ============ */}
      <section
        className="relative"
        style={{ paddingTop: "100px", paddingBottom: "100px" }}
        data-testid="engine-panels"
      >
        <div className="max-w-[1200px] mx-auto px-6">
          <Reveal>
            <h2
              className="font-bold text-center mx-auto max-w-3xl mb-20"
              style={{
                fontSize: "clamp(28px, 4.4vw, 48px)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
              }}
              data-testid="engine-panels-title"
            >
              The engine that actually{" "}
              <span style={{ color: GOLD }}>lands meetings.</span>
            </h2>
          </Reveal>
          <div className="space-y-24">
            {NUMBERED_PANELS.map((p, i) => (
              <NumberedPanel
                key={p.index}
                index={p.index}
                kicker={p.kicker}
                title={p.title}
                body={p.body}
                Icon={p.Icon}
                stat={p.stat}
                deliverables={p.deliverables}
                flip={i % 2 === 1}
                testid={`engine-panel-${p.index}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ VIDEO SHOWCASE . Watch it fire ============ */}
      <section
        className="relative"
        style={{ paddingTop: "80px", paddingBottom: "80px" }}
        data-testid="engine-video-showcase"
      >
        <div className="max-w-[1200px] mx-auto px-6">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <p
                  className="font-semibold"
                  style={{
                    fontSize: "12px",
                    letterSpacing: "0.14em",
                    color: GOLD,
                    textTransform: "uppercase",
                  }}
                >
                  Watch it fire
                </p>
                <h2
                  className="font-bold mt-4"
                  style={{
                    fontSize: "clamp(28px, 4.4vw, 48px)",
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                    maxWidth: "700px",
                  }}
                >
                  Three sends. <span style={{ color: GOLD }}>Same twin.</span>
                </h2>
              </div>
              <p style={{ fontSize: "14px", color: SLATE, maxWidth: "320px", lineHeight: 1.55 }}>
                Same face, same voice, three different prospects. Each opener rewritten. Each landing page swapped in the background.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {ENGINE_VIDEOS.map((v, i) => (
              <EngineVideoCard key={v.id} card={v} index={i} />
            ))}
          </div>
          <Reveal delay={0.2}>
            <p style={{ marginTop: "24px", fontSize: "12px", color: SLATE, textAlign: "center", letterSpacing: "0.05em" }}>
              Autoplays on scroll. 3 of 1,024 videos sent this month.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ 4 . SECURITY / TRUST STRIP ============ */}
      <section
        className="relative"
        style={{ paddingTop: "80px", paddingBottom: "80px" }}
        data-testid="engine-trust"
      >
        <div className="max-w-[1080px] mx-auto px-6">
          <Reveal>
            <h2
              className="font-bold text-center mx-auto max-w-3xl"
              style={{
                fontSize: "clamp(28px, 4.4vw, 48px)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
              }}
            >
              Built with{" "}
              <span style={{ color: GOLD }}>guardrails at the core.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                Icon: Sparkles,
                title: "Your voice. Your message.",
                body: "Every video carries your framing, your promise, your language. Brief us once and the twin represents you exactly as you want to be seen. No per-batch approvals, no bottlenecks.",
              },
              {
                Icon: ShieldCheck,
                title: "Your domain stays safe.",
                body: "We run sending infrastructure that protects your primary domain. Warm-up windows, spam-safe cadence, opt-outs honoured the first time asked. Your reputation compounds. Your domain never gets blacklisted.",
              },
              {
                Icon: Users,
                title: "No tools to buy. No setup on your side.",
                body: "Our team runs the entire stack for you. Sourcing, video, delivery, replies, dashboards, everything. Zero extra subscriptions, zero software to learn, zero surprise invoices. One monthly, all in.",
              },
            ].map((t) => (
              <Reveal key={t.title} testid={`engine-trust-${t.title.split(" ")[0].toLowerCase()}`}>
                <div
                  className="h-full p-8"
                  style={{
                    background: CARD_BG,
                    border: `1px solid ${CARD_BORDER}`,
                    borderRadius: "16px",
                  }}
                >
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center mb-5"
                    style={{
                      background: "rgba(201,169,97,0.15)",
                      border: `1px solid ${GOLD}`,
                    }}
                  >
                    <t.Icon className="h-4 w-4" style={{ color: GOLD_SOFT }} />
                  </div>
                  <p
                    className="font-semibold mb-2"
                    style={{
                      fontSize: "18px",
                      color: "#FFFFFF",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {t.title}
                  </p>
                  <p style={{ fontSize: "15px", lineHeight: 1.6, color: SLATE }}>{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 5 . PRICING TEASER (links to Growth Partnership) ============ */}
      <section
        className="relative"
        style={{ paddingTop: "80px", paddingBottom: "80px" }}
        data-testid="engine-pricing"
      >
        <div className="max-w-[720px] mx-auto px-6 text-center">
          <Reveal>
            <p
              className="font-semibold mb-4"
              style={{
                fontSize: "12px",
                letterSpacing: "0.1em",
                color: GOLD,
                textTransform: "uppercase",
              }}
            >
              Pricing
            </p>
            <h2
              className="font-bold"
              style={{
                fontSize: "clamp(28px, 4.4vw, 48px)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
              }}
            >
              Four ways to work with us.
            </h2>
            <p
              className="mt-5 mx-auto max-w-lg"
              style={{ fontSize: "16px", lineHeight: 1.6, color: SLATE }}
            >
              Pilot, Foundation, Growth, or Omnichannel. Every tier is fully managed end to end. See what fits your stage.
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-10 flex justify-center">
              <PrimaryCTA to="/growth-partnership" testid="engine-pricing-cta">
                See pricing on Growth Partnership
                <ArrowRight className="h-4 w-4" />
              </PrimaryCTA>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ 6 . BIG CTA BANNER ============ */}
      <section
        className="relative"
        style={{ paddingTop: "80px", paddingBottom: "80px" }}
        data-testid="engine-cta-banner"
      >
        <div className="max-w-[1080px] mx-auto px-6">
          <Reveal>
            <div
              className="relative overflow-hidden rounded-3xl text-center"
              style={{
                background:
                  "linear-gradient(135deg, #14100A 0%, #0A0A0A 60%, #14100A 100%)",
                border: `1px solid ${CARD_BORDER}`,
                padding: "clamp(48px, 8vw, 96px) 32px",
              }}
            >
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-40"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 60% at 50% 100%, rgba(201,169,97,0.28) 0%, transparent 70%)",
                }}
              />
              <p
                className="font-semibold mb-4"
                style={{ fontSize: "12px", letterSpacing: "0.1em", color: GOLD, textTransform: "uppercase" }}
              >
                Don&apos;t lose another quarter
              </p>
              <h2
                className="font-bold mx-auto max-w-3xl"
                style={{
                  fontSize: "clamp(32px, 5.4vw, 56px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.03em",
                }}
              >
                Hire the engine that runs your outbound for you.
              </h2>
              <div className="mt-10 flex justify-center">
                <PrimaryCTA to="/growth-partnership" testid="engine-banner-cta">
                  Start your build
                  <ArrowRight className="h-4 w-4" />
                </PrimaryCTA>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ 7 . FAQ ============ */}
      <section
        className="relative"
        style={{ paddingTop: "80px", paddingBottom: "80px" }}
        data-testid="engine-faq"
      >
        <div className="max-w-[720px] mx-auto px-6">
          <Reveal>
            <p
              className="font-semibold text-center mb-4"
              style={{
                fontSize: "12px",
                letterSpacing: "0.1em",
                color: GOLD,
                textTransform: "uppercase",
              }}
            >
              Quick answers
            </p>
            <h2
              className="font-bold text-center"
              style={{
                fontSize: "clamp(28px, 4.4vw, 48px)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
              }}
            >
              Everything you might be wondering.
            </h2>
          </Reveal>

          <div className="mt-12 space-y-3">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} testid={`engine-faq-${i}`}>
                <details
                  className="group"
                  style={{
                    background: CARD_BG,
                    border: `1px solid ${CARD_BORDER}`,
                    borderRadius: "16px",
                  }}
                >
                  <summary
                    className="cursor-pointer list-none flex items-center justify-between gap-6 p-6"
                  >
                    <span
                      className="font-medium"
                      style={{
                        fontSize: "18px",
                        color: "#FFFFFF",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {f.q}
                    </span>
                    <Plus
                      className="h-5 w-5 shrink-0 group-open:rotate-45 transition-transform duration-500"
                      style={{ color: GOLD }}
                    />
                  </summary>
                  <p
                    className="px-6 pb-6"
                    style={{ fontSize: "16px", lineHeight: 1.6, color: SLATE }}
                  >
                    {f.a}
                  </p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <StickyDiagnoseCTA />
    </main>
  );
}

const inputStyle = {
  background: "#1A1A1E",
  border: `1px solid ${CARD_BORDER}`,
  padding: "14px 20px",
  borderRadius: "8px",
  fontSize: "16px",
  fontFamily: "inherit",
  color: "#FFFFFF",
};

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span
        className="block mb-2"
        style={{ fontSize: "12px", letterSpacing: "0.05em", color: SLATE }}
      >
        {label}
        {required && <span style={{ color: GOLD, marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}
